import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { apiFetch } from '../services/auth'

interface User {
  id: string
  email: string
  name?: string
  displayName?: string
  role: 'lobby' | 'user' | 'admin' | 'lla'
  managerId?: string | null
  createdAt?: string
  updatedAt?: string
}

interface FortnoxEmployee {
  EmployeeId: string
  FirstName: string
  LastName: string
  Email?: string
}



export function UserManagement() {
  const { user: currentUser, refreshUserProfile } = useAuth()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [updatingUser, setUpdatingUser] = useState<string | null>(null)
  const [updatingManager, setUpdatingManager] = useState<string | null>(null)
  const [fortnoxEmployees, setFortnoxEmployees] = useState<FortnoxEmployee[]>([])
  const [loadingFortnoxEmployees, setLoadingFortnoxEmployees] = useState(false)
  const [syncingFortnoxEmployees, setSyncingFortnoxEmployees] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const response = await apiFetch('/user/users')

      if (!response.ok) {
        throw new Error(`Fel vid hämtning av användare: ${response.status}`)
      }

      const userData = await response.json()
      setUsers(userData)
    } catch (err) {
      // console.error('Error fetching users:', err)
      setError(err instanceof Error ? err.message : 'Okänt fel')
    } finally {
      setLoading(false)
    }
  }



  const updateUserRole = async (userId: string, newRole: 'lobby' | 'user' | 'admin' | 'lla') => {
    try {
      setUpdatingUser(userId)
      const response = await apiFetch(`/user/users/${userId}/role`, {
        method: 'PUT',
        body: JSON.stringify({ role: newRole })
      })

      if (!response.ok) {
        throw new Error(`Fel vid uppdatering av roll: ${response.status}`)
      }

      // Update local state
      setUsers(users.map(user => 
        user.id === userId ? { ...user, role: newRole } : user
      ))
      
      // If the current user's role was updated, refresh their profile to update menu access
      if (currentUser && userId === currentUser.id) {
        await refreshUserProfile()
      }
    } catch (err) {
      // console.error('Error updating user role:', err)
      setError(err instanceof Error ? err.message : 'Okänt fel vid uppdatering')
    } finally {
      setUpdatingUser(null)
    }
  }

  const syncFortnoxEmployees = async () => {
    try {
      setSyncingFortnoxEmployees(true)
      const response = await apiFetch('/user/sync-fortnox-employees', {
        method: 'POST',
      })

      if (!response.ok) {
        // console.error(`Failed to sync Fortnox employees: ${response.status}`)
        return
      }

      const syncResult = await response.json()
      // console.log('Fortnox sync result:', syncResult)
      
      // Refresh users list after sync
      await fetchUsers()
    } catch (err) {
      // console.error('Error syncing Fortnox employees:', err)
    } finally {
      setSyncingFortnoxEmployees(false)
    }
  }

  const fetchFortnoxEmployees = async () => {
    try {
      setLoadingFortnoxEmployees(true)
      const response = await apiFetch('/staff')

      if (!response.ok) {
        // console.error(`Failed to fetch Fortnox employees: ${response.status}`)
        return
      }

      const staffData = await response.json()
      setFortnoxEmployees(staffData.employees || [])
    } catch (err) {
      // console.error('Error fetching Fortnox employees:', err)
    } finally {
      setLoadingFortnoxEmployees(false)
    }
  }

  const updateUserManager = async (userId: string, managerId: string | null) => {
    try {
      setUpdatingManager(userId)
      const response = await apiFetch(`/user/users/${userId}/manager`, {
        method: 'PUT',
        body: JSON.stringify({ managerId })
      })

      if (!response.ok) {
        throw new Error(`Fel vid uppdatering av chef: ${response.status}`)
      }

      // Update local state
      setUsers(users.map(user => 
        user.id === userId ? { ...user, managerId } : user
      ))
    } catch (err) {
      // console.error('Error updating user manager:', err)
      setError(err instanceof Error ? err.message : 'Okänt fel vid uppdatering')
    } finally {
      setUpdatingManager(null)
    }
  }



  useEffect(() => {
    const initializeData = async () => {
      // Always fetch users first
      await fetchUsers()
      
      // If user is admin, sync Fortnox employees first, then fetch them for display
      if (currentUser?.role === 'admin') {
        await syncFortnoxEmployees()
      }
      
      // Fetch Fortnox employees for connection status display
      await fetchFortnoxEmployees()
    }
    
    initializeData()
  }, [currentUser?.role])

  const isCurrentUser = (user: User) => {
    return user.id === currentUser?.id || user.email === currentUser?.email
  }

  const getRoleBadgeStyle = (role: string) => {
    switch (role) {
      case 'admin':
        return { backgroundColor: '#dc2626', color: 'white' }
      case 'lla':
        return { backgroundColor: '#7c3aed', color: 'white' }
      case 'lobby':
        return { backgroundColor: '#f59e0b', color: 'white' }
      default:
        return { backgroundColor: '#059669', color: 'white' }
    }
  }

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Administratör'
      case 'lla':
        return 'LLA'
      case 'lobby':
        return 'Lobby'
      default:
        return 'Användare'
    }
  }

  const getFortnoxConnectionStatus = (userEmail: string) => {
    if (loadingFortnoxEmployees) {
      return { status: 'loading', employee: null }
    }
    
    const matchedEmployee = fortnoxEmployees.find(emp => 
      emp.Email && emp.Email.toLowerCase() === userEmail.toLowerCase()
    )
    
    if (matchedEmployee) {
      return { status: 'connected', employee: matchedEmployee }
    }
    
    return { status: 'not_connected', employee: null }
  }



  if (loading || syncingFortnoxEmployees) {
    return (
      <div style={{
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e2e8f0',
        textAlign: 'center'
      }}>
        <p>{syncingFortnoxEmployees ? 'Synkroniserar Fortnox-anställda...' : 'Laddar användare...'}</p>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e2e8f0',
        textAlign: 'center'
      }}>
        <p style={{ color: '#dc2626' }}>Fel: {error}</p>
        <button
          onClick={fetchUsers}
          style={{
            marginTop: '16px',
            padding: '8px 16px',
            backgroundColor: '#2563eb',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          Försök igen
        </button>
      </div>
    )
  }

  return (
    <div style={{
      backgroundColor: 'white',
      padding: isMobile ? '16px' : '24px',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0'
    }}>
      <h3 style={{ margin: '0 0 24px 0', color: '#1a202c', fontSize: isMobile ? '16px' : '18px' }}>Användarhantering</h3>
      
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: isMobile ? '14px' : '16px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
              <th style={{ padding: isMobile ? '8px' : '12px', textAlign: 'left', color: '#64748b', fontWeight: '500', fontSize: isMobile ? '12px' : '14px' }}>Namn</th>
              <th style={{ padding: isMobile ? '8px' : '12px', textAlign: 'left', color: '#64748b', fontWeight: '500', fontSize: isMobile ? '12px' : '14px' }}>E-post</th>
              <th style={{ padding: isMobile ? '8px' : '12px', textAlign: 'left', color: '#64748b', fontWeight: '500', fontSize: isMobile ? '12px' : '14px' }}>Roll</th>
              <th style={{ padding: isMobile ? '8px' : '12px', textAlign: 'left', color: '#64748b', fontWeight: '500', fontSize: isMobile ? '12px' : '14px' }}>Chef</th>
              <th style={{ padding: isMobile ? '8px' : '12px', textAlign: 'left', color: '#64748b', fontWeight: '500', fontSize: isMobile ? '12px' : '14px' }}>Fortnoxkoppling</th>
              <th style={{ padding: isMobile ? '8px' : '12px', textAlign: 'left', color: '#64748b', fontWeight: '500', fontSize: isMobile ? '12px' : '14px' }}>Åtgärder</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: isMobile ? '8px' : '12px', color: '#1a202c', fontSize: isMobile ? '13px' : '14px' }}>
                  {user.displayName || user.name || 'Ej angivet'}
                </td>
                <td style={{ padding: isMobile ? '8px' : '12px', color: '#1a202c', fontSize: isMobile ? '13px' : '14px' }}>
                  {user.email}
                </td>
                <td style={{ padding: isMobile ? '8px' : '12px' }}>
                  <span style={{
                    ...getRoleBadgeStyle(user.role),
                    padding: isMobile ? '3px 6px' : '4px 8px',
                    borderRadius: '4px',
                    fontSize: isMobile ? '11px' : '12px',
                    fontWeight: '500'
                  }}>
                    {getRoleLabel(user.role)}
                  </span>
                </td>
                <td style={{ padding: isMobile ? '8px' : '12px' }}>
                  <select
                    value={user.managerId || ''}
                    onChange={(e) => updateUserManager(user.id, e.target.value || null)}
                    disabled={updatingManager === user.id}
                    style={{
                      padding: isMobile ? '8px' : '4px 8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: isMobile ? '14px' : '14px',
                      backgroundColor: updatingManager === user.id ? '#f3f4f6' : 'white',
                      cursor: updatingManager === user.id ? 'not-allowed' : 'pointer',
                      minHeight: isMobile ? '40px' : 'auto'
                    }}
                  >
                        <option value="">Ingen chef</option>
                        {users
                          .filter(u => u.id !== user.id && (u.role === 'admin' || u.role === 'lla'))
                          .map(manager => (
                            <option key={manager.id} value={manager.id}>
                              {manager.displayName || manager.name || manager.email}
                            </option>
                          ))
                        }                  </select>
                  {updatingManager === user.id && (
                    <span style={{ 
                      marginLeft: isMobile ? '4px' : '8px', 
                      fontSize: isMobile ? '11px' : '12px', 
                      color: '#64748b',
                      display: isMobile ? 'block' : 'inline',
                      marginTop: isMobile ? '4px' : '0'
                    }}>
                      Uppdaterar...
                    </span>
                  )}
                </td>
                <td style={{ padding: isMobile ? '8px' : '12px' }}>
                  {(() => {
                    const connectionStatus = getFortnoxConnectionStatus(user.email)
                    
                    if (connectionStatus.status === 'loading') {
                      return (
                        <span style={{
                          fontSize: isMobile ? '12px' : '14px',
                          color: '#64748b'
                        }}>
                          Laddar...
                        </span>
                      )
                    }
                    
                    if (connectionStatus.status === 'connected' && connectionStatus.employee) {
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#16a34a'
                          }} />
                          <span style={{
                            fontSize: isMobile ? '12px' : '14px',
                            color: '#16a34a',
                            fontWeight: '500'
                          }}>
                            {connectionStatus.employee.FirstName} {connectionStatus.employee.LastName}
                          </span>
                        </div>
                      )
                    }
                    
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: '#dc2626'
                        }} />
                        <span style={{
                          fontSize: isMobile ? '12px' : '14px',
                          color: '#64748b'
                        }}>
                          Ingen koppling
                        </span>
                      </div>
                    )
                  })()}
                </td>
                <td style={{ padding: isMobile ? '8px' : '12px' }}>
                  {isCurrentUser(user) ? (
                    <span style={{
                      padding: isMobile ? '6px 8px' : '4px 8px',
                      backgroundColor: '#f3f4f6',
                      color: '#64748b',
                      borderRadius: '4px',
                      fontSize: isMobile ? '12px' : '14px',
                      border: '1px solid #d1d5db'
                    }}>
                      {getRoleLabel(user.role)} (Du)
                    </span>
                  ) : (
                    <>
                      <select
                        value={user.role}
                        onChange={(e) => updateUserRole(user.id, e.target.value as 'lobby' | 'user' | 'admin' | 'lla')}
                        disabled={updatingUser === user.id}
                        style={{
                          padding: isMobile ? '8px' : '4px 8px',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: isMobile ? '14px' : '14px',
                          backgroundColor: updatingUser === user.id ? '#f3f4f6' : 'white',
                          cursor: updatingUser === user.id ? 'not-allowed' : 'pointer',
                          minHeight: isMobile ? '40px' : 'auto'
                        }}
                       >
                         <option key="lobby" value="lobby">Lobby</option>
                         <option key="user" value="user">Användare</option>
                         <option key="admin" value="admin">Administratör</option>
                         <option key="lla" value="lla">LLA</option>
                       </select>                      {updatingUser === user.id && (
                        <span style={{ 
                          marginLeft: isMobile ? '4px' : '8px', 
                          fontSize: isMobile ? '11px' : '12px', 
                          color: '#64748b',
                          display: isMobile ? 'block' : 'inline',
                          marginTop: isMobile ? '4px' : '0'
                        }}>
                          Uppdaterar...
                        </span>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {users.length === 0 && (
        <p style={{ textAlign: 'center', color: '#64748b', margin: '40px 0' }}>
          Inga användare hittades.
        </p>
      )}
    </div>
  )
}
